import cookie from 'react-cookie';
import * as Sentry from '@sentry/react';
import { getCelloReferralTrackingId } from './celloReferral';

const UTM_FIELD_MAX_LEN = 512;

function trimField(value) {
  if (value == null) {
    return '';
  }
  let s = String(value).trim();
  if (s.length > UTM_FIELD_MAX_LEN) {
    s = s.slice(0, UTM_FIELD_MAX_LEN);
  }
  return s;
}

/**
 * Resolve Cello UCC: CelloAttribution('getUcc') when available, else first-party cookie.
 *
 * @returns {Promise<string>}
 */
async function resolveCelloUcc() {
  let ucc = '';
  if (
    typeof window !== 'undefined' &&
    typeof window.CelloAttribution === 'function'
  ) {
    try {
      const fromSdk = await window.CelloAttribution('getUcc');
      ucc = trimField(fromSdk);
    } catch (error) {
      Sentry.captureException(
        error instanceof Error
          ? error
          : new Error('Cello attribution getUcc failed'),
        {
          tags: { area: 'signup', integration: 'cello' },
          extra: { rawReason: error },
        }
      );
    }
  }
  if (!ucc) {
    ucc = trimField(getCelloReferralTrackingId() || '');
  }
  return ucc;
}

/**
 * UTM payload for signup (passwordless and Google SSO).
 * Matches legacy SignUp order: cookie UTM fields, then gclid/fbclid for tracking_id,
 * else Cello (overwrites source/medium/campaign/tracking_id when UCC present).
 *
 * @returns {Promise<Object>}
 */
export async function buildSignupAttributionUtm() {
  const utm = {};

  const utmSource = trimField(cookie.load('utm_source'));
  const utmMedium = trimField(cookie.load('utm_medium'));
  const utmCampaign = trimField(cookie.load('utm_campaign'));
  const utmTerm = trimField(cookie.load('utm_term'));
  const utmContent = trimField(cookie.load('utm_content'));
  const briefLink = trimField(cookie.load('brief-link'));
  const gclid = trimField(cookie.load('gclid'));
  const fbclid = trimField(cookie.load('fbclid'));

  const ucc = await resolveCelloUcc();

  if (utmSource) {
    utm.source = utmSource;
  }
  if (utmMedium) {
    utm.medium = utmMedium;
  }
  if (utmCampaign) {
    utm.campaign = utmCampaign;
  }
  if (utmTerm) {
    utm.term = utmTerm;
  }
  if (utmContent) {
    utm.content = utmContent;
  } else if (briefLink) {
    utm.content = briefLink;
  }

  if (gclid) {
    utm.tracking_id = gclid;
  } else if (fbclid) {
    utm.tracking_id = fbclid;
  } else if (ucc) {
    utm.source = 'cello';
    utm.medium = 'referral';
    utm.campaign = 'cello-referral';
    utm.tracking_id = ucc;
  }

  return utm;
}
